import { useAuth } from '@/hooks/useAuth';
import { usePaddleCheckout } from '@/hooks/usePaddleCheckout';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sprout, Crown, Check, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpgradeDialog({ open, onOpenChange }: UpgradeDialogProps) {
  const { user } = useAuth();
  const { openCheckout, loading } = usePaddleCheckout();
  const { subscription, isPro } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);

  const handleSubscribe = async (priceId: string) => {
    if (!user) return;
    await openCheckout({
      priceId,
      customerEmail: user.email,
      customData: { userId: user.id },
    });
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { returnUrl: window.location.href },
      });
      if (error || !data?.url) throw new Error('Could not open portal');
      window.open(data.url, '_blank');
    } catch {
      toast.error('Could not open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (isPro) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" /> Pro Plan Active
            </DialogTitle>
            <DialogDescription>
              You have unlimited plants! {subscription?.cancel_at_period_end && '(Cancels at end of period)'}
            </DialogDescription>
          </DialogHeader>
          <Button onClick={handleManageSubscription} disabled={portalLoading} variant="outline" className="w-full">
            <ExternalLink className="h-4 w-4 mr-2" />
            {portalLoading ? 'Opening...' : 'Manage Subscription'}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Sprout className="h-5 w-5 text-primary" /> Upgrade to Pro
          </DialogTitle>
          <DialogDescription>
            You've reached the free limit of 5 plants. Upgrade for unlimited plants!
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 mt-2">
          <Card className="border-2 border-primary/30 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Badge className="bg-primary text-primary-foreground text-[10px]">Popular</Badge>
            </div>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-foreground">£3.99</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-1.5 my-3">
                {['Unlimited plants', 'Full schedule generation', 'All plant types'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="h-3.5 w-3.5 text-primary" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleSubscribe('pro_monthly')}
                disabled={loading}
                className="w-full garden-gradient"
              >
                {loading ? 'Loading...' : 'Subscribe Monthly'}
              </Button>
            </CardContent>
          </Card>

          <Card className="border-2 border-border">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-foreground">£33</span>
                <span className="text-sm text-muted-foreground">/year</span>
              </div>
              <p className="text-xs text-primary font-medium mb-3">Save 31% vs monthly</p>
              <Button
                onClick={() => handleSubscribe('pro_annual')}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                {loading ? 'Loading...' : 'Subscribe Annually'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
